import type { ShopDisapprovalReasonDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Archive, Ellipsis, GripVertical, Pencil } from "lucide-react";

type Props = {
  reason: ShopDisapprovalReasonDto;
  isBusy: boolean;
  onEdit: () => void;
  onArchive: () => void;
};

export function DisapprovalReasonRow({
  reason,
  isBusy,
  onEdit,
  onArchive,
}: Props) {
  const t = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: reason.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="group flex items-center gap-3 border-b border-border py-3 last:border-b-0"
    >
      <button
        type="button"
        {...listeners}
        {...attributes}
        aria-label={t("settings.disapprovalReasons.actions.reorder")}
        className="cursor-grab p-1 text-muted-foreground opacity-50 transition-opacity active:cursor-grabbing group-hover:opacity-100"
      >
        <GripVertical className="size-4" />
      </button>

      <div className="flex min-w-0 flex-1 flex-col">
        <p className="truncate text-sm font-medium">{reason.label}</p>
        {reason.help && (
          <p className="truncate text-xs text-muted-foreground">{reason.help}</p>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" disabled={isBusy}>
            <Ellipsis className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onEdit}>
            <Pencil className="size-4" />
            {t("misc.edit")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onArchive}>
            <Archive className="size-4" />
            {t("settings.disapprovalReasons.actions.archive")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
